import './DetailedRatingsPopper.scss'

import { createPopper } from '@popperjs/core'

import { InternalComponentAPI, InternalPluginProps } from '../../utils/types'
import { closingOf } from '../../utils/classNames'
import { addEventListener } from '../../utils/events'
import { withClass, withClosingClassOf } from '../../utils/selectors'
import { appendElement } from '../../utils/dom'
import DetailedRatings from '../DetailedRatings/DetailedRatings'

const DETAILED_RATINGS_POPPER_CLASS = 'detailed-ratings-popper'
const DETAILED_RATINGS_POPPER_CONTENT_CLASS = 'detailed-ratings-popper__content'
const SHOW_POPPER_DATA_ATTRIB = 'data-show-popper'

type DetailedRatingsPopperProps = InternalPluginProps & {
  dialogParent: Element
  anchor: Element
  onSubmitRatingClick: (event: Event) => void
  onPopoverStateChange?(open: boolean): void
}

export type DetailedRatingsPopperAPI = InternalComponentAPI & {
  show(): void
  hide(): void
}

const DetailedRatingsPopper = ({
  dialogParent,
  anchor,
  onSubmitRatingClick,
  onPopoverStateChange,
  ...props
}: DetailedRatingsPopperProps): DetailedRatingsPopperAPI => {
  // Build HTML
  const popper = appendElement({
    parent: dialogParent,
    html: detailedRatingsDialog()
  }) as HTMLElement

  // Init popper lib
  createPopper(anchor, popper, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      }
    ]
  })

  // Event listners
  const showPopper = () => {
    popper.setAttribute(SHOW_POPPER_DATA_ATTRIB, '')
    onPopoverStateChange && onPopoverStateChange(true)
  }
  const hidePopper = () => {
    popper.removeAttribute(SHOW_POPPER_DATA_ATTRIB)
    onPopoverStateChange && onPopoverStateChange(false)
  }
  const onSubmitRatingPopperClick = (event: Event) => {
    hidePopper()
    onSubmitRatingClick(event)
  }

  // Click on close button
  addEventListener({
    target: popper,
    eventName: 'click',
    selector: withClosingClassOf(DETAILED_RATINGS_POPPER_CLASS),
    handler: hidePopper
  })

  // Init child components
  const detailedRatings = DetailedRatings({
    ...props,
    target: popper.querySelector(withClass(DETAILED_RATINGS_POPPER_CONTENT_CLASS))!,
    onSubmitRatingClick: onSubmitRatingPopperClick
  })

  // Public API
  return {
    show: showPopper,
    hide: hidePopper,
    updateRatings: detailedRatings.updateRatings,
    destroy: () => {
      detailedRatings.destroy()
      popper.innerHTML = ''
    }
  }
}

export default DetailedRatingsPopper

//
// Templates
//

const detailedRatingsDialog = () => `
  <div class="${DETAILED_RATINGS_POPPER_CLASS}">
    <button
      class="${closingOf(DETAILED_RATINGS_POPPER_CLASS)}"
      aria-label="Close ratings dialog"
    >
    </button>
    <div class="${DETAILED_RATINGS_POPPER_CONTENT_CLASS}"></div>
  </div>
`
